import React, { PropsWithChildren, createContext, useContext, useMemo } from 'react';

import { PlayerWithTeam } from '@/feature/betslip-pickem/types';

import { useLobbyPlayers, usePopularPlayers } from './use-fantasy-lobby-query-with-variables';

type PopularPlayersContextValue = {
    trendingPlayers: PlayerWithTeam[];
    popularPlayers: PlayerWithTeam[];
    fetchingTrendingPlayers: boolean;
    fetchingPopularPlayers: boolean;
};

const PopularPlayersContext = createContext<PopularPlayersContextValue | undefined>(undefined);

/**
 * Reads popular and trending players from cache and shares them via context
 *
 */
export const PopularPlayersDataProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { trendingPlayers, fetching: fetchingTrendingPlayers } = useLobbyPlayers({
        requestPolicy: 'cache-only',
    });
    const { popularPlayers, fetching: fetchingPopularPlayers } = usePopularPlayers({
        requestPolicy: 'cache-only',
    });

    const value = useMemo(
        () => ({ trendingPlayers, popularPlayers, fetchingTrendingPlayers, fetchingPopularPlayers }),
        [trendingPlayers, popularPlayers, fetchingTrendingPlayers, fetchingPopularPlayers]
    );

    return <PopularPlayersContext.Provider value={value}>{children}</PopularPlayersContext.Provider>;
};

export const usePopularPlayersData = (): PopularPlayersContextValue => {
    const context = useContext(PopularPlayersContext);
    if (context === undefined) {
        throw new Error('usePopularPlayersData must be used within a PopularPlayersDataProvider');
    }
    return context;
};
